import React, { useEffect, useState } from 'react'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Icon,
  Popover,
  Select,
  Textfield
} from '@clientbase/clientbase-library'
import {
  MenuItem,
  SelectChangeEvent,
  AlertColor,
  useMediaQuery
} from '@mui/material'
import { ParsedUrlQuery } from 'querystring'

import { Order, OrderParams, OrderState } from 'components/Table'

import { handleDeleteQueryFilters } from 'utils/query-string'

import * as S from './ModalPaymentLinkReport.styles'
export type FiltersCobrancaParams = {
  customer_name?: string
  amoun?: string
  status?: string
}

const status = [
  { label: 'Pendente', value: 'pending' },
  { label: 'Ativa', value: 'active' },
  { label: 'Finalizada', value: 'finished' },
  { label: 'Cancelada', value: 'cancelled' }
]

const filtersTextfields = [
  {
    name: 'customer_name',
    label: 'Nome:'
  },

  {
    name: 'amount',
    label: 'Valor:'
  }
]

export type Alert = {
  severity: AlertColor
  title?: string
  message: string
}

type ReportRecurrenceFiltersProps = {
  baseFilters: { [key in string]: string } | ParsedUrlQuery
  setBaseFilters: (params: {
    [key in string]: string | number | object
  }) => void
}

const ReportRecurrenceFilters = ({
  baseFilters,
  setBaseFilters
}: ReportRecurrenceFiltersProps) => {
  const [sortCharge, setSortCharge] = useState<OrderState>({
    label: 'due_day',
    sort: 'desc'
  })

  const [filters, setFilters] = useState<FiltersCobrancaParams>(
    {} as FiltersCobrancaParams
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleOnChangeOrder = (value: string, order: OrderParams) => {
    const newValue = { label: value, sort: order }
    setSortCharge(newValue)

    if (value === 'initial') {
      // eslint-disable-next-line
      const { sort, ...filtersWithoutSort } = baseFilters
      setBaseFilters({ query: filtersWithoutSort })
      return
    }

    setBaseFilters({ query: { ...baseFilters, sort: `${value}:${order}` } })
  }

  const handleClearMenagementType = () => {
    // eslint-disable-next-line
    const { scheduled, issued, ...rest } = baseFilters
    return rest
  }

  const handleDeleteFilter = (item: string) => {
    const currencyFilter = handleDeleteQueryFilters({
      filters: baseFilters,
      options: [item]
    })
    setAnchorEl(null)
    setBaseFilters({ query: currencyFilter })
  }

  const handleChangeTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target
    setFilters({ ...filters, [name]: value })
  }

  const handleChangeSelect = (
    event: SelectChangeEvent<unknown>,
    key: string
  ) => {
    const value = event.target.value as string
    setFilters({ ...filters, [key]: value })
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleFilter = () => {
    const newFilters = handleClearMenagementType()
    setBaseFilters({ query: { ...newFilters, ...filters } })
    handleClose()
  }
  useEffect(() => {
    handleClearFilters()
  }, [])

  const handleClearFilters = () => {
    const currencyFilter = handleDeleteQueryFilters({
      filters: baseFilters,
      options: ['customer_name', 'amount', 'status']
    })

    setBaseFilters({ query: currencyFilter })
    handleClose()
  }

  useEffect(() => {
    setFilters({
      ...(baseFilters?.customer_name && {
        customer_name: baseFilters?.customer_name
      }),
      ...(baseFilters?.amount && {
        amount: baseFilters?.amount
      }),
      ...(baseFilters?.status && { status: baseFilters?.status })
    } as FiltersCobrancaParams)
  }, [baseFilters])

  useEffect(() => {
    if (baseFilters.sort) {
      const [value, order] = (baseFilters.sort as string).split(':')
      const newValue = {
        label: value || 'initial',
        sort: (order || 'desc') as OrderParams
      }
      setSortCharge(newValue)
    }
  }, [])

  const isMobile = useMediaQuery('(max-width: 600px)', { noSsr: false })

  return (
    <div>
      <div>
        <S.ButtonsWrapper>
          <Order
            handleOnChange={handleOnChangeOrder}
            options={[
              { label: 'Organizar por:', value: 'initial' },
              { label: 'Por vencimento', value: 'due_day' },
              { label: 'Por valor cobrado', value: 'amount' },
              { label: 'Por status', value: 'status' }
            ]}
            value={sortCharge}
          />

          <Button
            aria-controls={open ? 'button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            id="button-filters"
            onClick={handleClick}
            variantButton="neutral"
            fullWidth={isMobile}
          >
            Filtros: {Object.keys(filters).length}
          </Button>
        </S.ButtonsWrapper>
        <Popover
          anchorEl={anchorEl}
          id="menu-filters"
          onClose={handleClose}
          open={open}
        >
          <S.PopoverContainer>
            <S.ScrollableContent>
              {filtersTextfields.map(({ name, label }) => (
                <React.Fragment key={name}>
                  <Accordion>
                    <AccordionSummary>
                      <S.AccordionSummaryContent>
                        &#8288;{label}
                        {!!filters[name] && (
                          <Icon
                            icon="cancel"
                            onClick={() => handleDeleteFilter(name)}
                            width="20px"
                            sx={{ mr: 1 }}
                          />
                        )}
                      </S.AccordionSummaryContent>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Textfield
                        name={name}
                        onChange={handleChangeTextField}
                        value={filters[name]}
                      />
                    </AccordionDetails>
                  </Accordion>
                </React.Fragment>
              ))}

              <Accordion>
                <AccordionSummary>
                  <S.AccordionSummaryContent>
                    Status:
                    {!!filters?.status && (
                      <Icon
                        icon="cancel"
                        onClick={() => handleDeleteFilter('status')}
                        width="20px"
                        sx={{ mr: 1 }}
                      />
                    )}
                  </S.AccordionSummaryContent>
                </AccordionSummary>
                <AccordionDetails>
                  <Select
                    fullWidth={true}
                    name="status"
                    onChange={(e) => handleChangeSelect(e, 'status')}
                    value={filters.status}
                  >
                    {status.map(({ label, value }, index) => (
                      <MenuItem key={index} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </AccordionDetails>
              </Accordion>
            </S.ScrollableContent>

            <S.FixedButtonsContainer>
              <Button
                variantButton="neutral"
                fullWidth
                onClick={handleClearFilters}
              >
                Limpar
              </Button>
              <Button fullWidth onClick={handleFilter}>
                Filtrar
              </Button>
            </S.FixedButtonsContainer>
          </S.PopoverContainer>
        </Popover>
      </div>
    </div>
  )
}

export default ReportRecurrenceFilters
